import { ArrowDownward, ArrowUpward, Delete, Add } from '@mui/icons-material';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  IconButton,
  TextField,
} from '@mui/material';
import React from 'react';
import { FAQData } from '../../../components/faq/types';
import { StyledButton } from '../../../components/styles/StyledButtons';
import { StyledHeading, StyledTypography } from '../../../components/styles/styles';

interface FAQAdminGroupsListProps {
  data: FAQData;
  selectedGroup: number;
  selectedItem: number;
  onSelectGroup: (index: number) => void;
  onSelectItem: (index: number) => void;
  onMoveGroup: (from: number, to: number) => void;
  onRemoveGroup: (index: number) => void;
  onUpdateGroupName: (groupIdx: number, name: string) => void;
  onAddItem: (groupIdx: number) => void;
  onMoveItem: (groupIdx: number, from: number, to: number) => void;
  onRemoveItem: (groupIdx: number, itemIdx: number) => void;
}

const FAQAdminGroupsList: React.FC<FAQAdminGroupsListProps> = ({
  data,
  selectedGroup,
  selectedItem,
  onSelectGroup,
  onSelectItem,
  onMoveGroup,
  onRemoveGroup,
  onUpdateGroupName,
  onAddItem,
  onMoveItem,
  onRemoveItem,
}) => (
  <Box sx={{ flex: 1, minWidth: 320 }}>
    <StyledHeading headerVariant="subheading">Groups</StyledHeading>
    {data.groups.map((g, gi) => (
      <Card
        key={gi}
        sx={{
          mb: 1,
          border: gi === selectedGroup ? '2px solid #1976d2' : '1px solid #ddd',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CardActionArea
              onClick={() => {
                onSelectGroup(gi);
                onSelectItem(0);
              }}
            >
              <Box sx={{ p: 1 }}>
                <StyledTypography tone="default" fontWeight={600}>
                  {g.group}
                </StyledTypography>
              </Box>
            </CardActionArea>
            <IconButton
              size="small"
              onClick={() => onMoveGroup(gi, gi - 1)}
              aria-label="move-group-up"
              disabled={gi === 0}
            >
              <ArrowUpward />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onMoveGroup(gi, gi + 1)}
              aria-label="move-group-down"
              disabled={gi === data.groups.length - 1}
            >
              <ArrowDownward />
            </IconButton>
            <IconButton onClick={() => onRemoveGroup(gi)} aria-label="delete-group">
              <Delete />
            </IconButton>
          </Box>
          {gi === selectedGroup && (
            <Box sx={{ mt: 1 }}>
              <TextField
                label="Group name"
                size="small"
                fullWidth
                value={g.group}
                onChange={(e) => onUpdateGroupName(gi, e.target.value)}
              />
              <Divider sx={{ my: 1 }} />
              <StyledButton variant="outlined" startIcon={<Add />} onClick={() => onAddItem(gi)}>
                Add Item
              </StyledButton>
              <Box sx={{ mt: 1 }}>
                {g.items.map((item, ii) => (
                  <Card
                    key={ii}
                    sx={{
                      mb: 1,
                      border: ii === selectedItem ? '2px solid #1976d2' : '1px solid #eee',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <CardActionArea onClick={() => onSelectItem(ii)}>
                          <Box sx={{ p: 1 }}>
                            <StyledTypography tone="default" fontSize="0.95rem">
                              {item.question}
                            </StyledTypography>
                          </Box>
                        </CardActionArea>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => onMoveItem(gi, ii, ii - 1)}
                            aria-label="move-item-up"
                            disabled={ii === 0}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onMoveItem(gi, ii, ii + 1)}
                            aria-label="move-item-down"
                            disabled={ii === g.items.length - 1}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                          <IconButton onClick={() => onRemoveItem(gi, ii)} aria-label="delete-item">
                            <Delete />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    ))}
  </Box>
);

export default FAQAdminGroupsList;
